import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

var _CSS$supports, _CSS;

var _excluded = ["isPseudoFlex"];
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import * as SS from 'styled-system';
import Flex from '../Flex/Flex';
import { isIE } from '../helpers/constants';
import { getOmittableProps } from '../helpers/omit';
import { useMergedRefs } from '../helpers/react';
import { screenSizes, useScreenSize } from '../ScreenSizeProvider/ScreenSizeProvider';
var DEFAULT_GAP = 4;
var PSEUDO_FLEX_COLS = 12;
var COLUMN_NUMS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]; // Typescript (rightly) dislikes IE property names, so...

var css = {
  grid: '-ms-grid',
  gridTemplateRows: '-ms-grid-rows',
  gridTemplateColumns: '-ms-grid-columns',
  gridRowStart: '-ms-grid-row',
  gridColumnStart: '-ms-grid-column',
  // ...not really true, but as close as we can get.
  gridRowEnd: '-ms-grid-row-span',
  gridColumnEnd: '-ms-grid-column-span',
  justifySelf: '-ms-grid-column-align',
  alignSelf: '-ms-grid-row-align'
};

if (!isIE) {
  Object.keys(css).forEach(function (key) {
    return css[key] = key;
  });
}

var gridItemStyles = function () {
  var itemStyleConfig;

  if (isIE) {
    var delimiter = /\s*\/\s*/;
    var spanRegex = /span/i;

    var getStartSpan = function getStartSpan(startProp, spanProp, start, end) {
      var styles = {};

      if (end) {
        if (spanRegex.test(end)) {
          styles[spanProp] = String(parseInt(end.replace(spanRegex, '')));
        } else {
          styles[spanProp] = String(parseInt(end) - parseInt(start));
        }
      }

      styles[startProp] = start;
      return styles;
    };

    var getCombinedStartEndParser = function getCombinedStartEndParser(startProp, spanProp) {
      return function (spec) {
        if (spec) {
          if (typeof spec === 'number') {
            return getStartSpan(startProp, spanProp, String(spec));
          } else if (typeof spec === 'string') {
            var _spec$split = spec.split(delimiter),
                _spec$split2 = _slicedToArray(_spec$split, 2),
                start = _spec$split2[0],
                end = _spec$split2[1];

            return getStartSpan(startProp, spanProp, start, end);
          }
        }
      };
    };

    var getStartParser = function getStartParser(startProp) {
      return function (start) {
        if (start) {
          return _defineProperty({}, startProp, String(start));
        }
      };
    };

    var getEndParser = function getEndParser(spanProp) {
      return function (span, _, props) {
        if (span) {
          var end = span;

          if (typeof span === 'string') {
            if (spanRegex.test(span)) {
              span = span.replace(spanRegex, '').trim();
            } else {
              end = parseInt(span);
            }
          }

          if (typeof end === 'number') {
            var start = spanProp === css.gridRowEnd ? props.rowStart : props.colStart || props.columnStart;
            span = end - (parseInt(start) || 1);
          }

          return _defineProperty({}, spanProp, String(span));
        }
      };
    };

    itemStyleConfig = {
      row: getCombinedStartEndParser(css.gridRowStart, css.gridRowEnd),
      rowStart: getStartParser(css.gridRowStart),
      rowEnd: getEndParser(css.gridRowEnd),
      col: getCombinedStartEndParser(css.gridColumnStart, css.gridColumnEnd),
      colStart: getStartParser(css.gridColumnStart),
      colEnd: getEndParser(css.gridColumnEnd),
      gridArea: function gridArea(areaSpec) {
        if (areaSpec && typeof areaSpec === 'string') {
          var _areaSpec$split = areaSpec.split(delimiter),
              _areaSpec$split2 = _slicedToArray(_areaSpec$split, 4),
              rowStart = _areaSpec$split2[0],
              colStart = _areaSpec$split2[1],
              rowEnd = _areaSpec$split2[2],
              colEnd = _areaSpec$split2[3];

          var rowStyles = getStartSpan(css.gridRowStart, css.gridRowEnd, rowStart, rowEnd);
          var colStyles = getStartSpan(css.gridColumnStart, css.gridColumnEnd, colStart, colEnd);
          return Object.assign(rowStyles, colStyles);
        }
      },
      justifySelf: {
        property: css.justifySelf
      },
      alignSelf: {
        property: css.alignSelf
      }
    };
  } else {
    // Stringify to ensure grid lines won't be treated like CSS <length> values.
    var transform = function transform(val) {
      return typeof val === 'number' ? String(val) : val;
    };

    itemStyleConfig = {
      row: {
        property: 'gridRow',
        transform: transform
      },
      rowStart: {
        property: 'gridRowStart',
        transform: transform
      },
      rowEnd: {
        property: 'gridRowEnd',
        transform: transform
      },
      col: {
        property: 'gridColumn',
        transform: transform
      },
      colStart: {
        property: 'gridColumnStart',
        transform: transform
      },
      colEnd: {
        property: 'gridColumnEnd',
        transform: transform
      },
      gridArea: {
        property: 'gridArea',
        transform: transform
      },
      justifySelf: true
    };
  }

  itemStyleConfig.column = itemStyleConfig.col;
  itemStyleConfig.columnStart = itemStyleConfig.colStart;
  itemStyleConfig.columnEnd = itemStyleConfig.colEnd;
  return SS.system(itemStyleConfig);
}();

var pseudoFlexStyles = function () {
  var parser = function parser(props) {
    var spans = screenSizes.map(function (size) {
      return props[size] && "span ".concat(props[size]);
    });
    return gridItemStyles({
      colEnd: spans,
      theme: props.theme
    });
  };

  parser.propNames = _toConsumableArray(screenSizes);
  return parser;
}();

var itemStyleProps = getOmittableProps(gridItemStyles, pseudoFlexStyles);
export var GridItem = /*#__PURE__*/styled(Flex.Item).withConfig({
  shouldForwardProp: function shouldForwardProp(p) {
    return !itemStyleProps.has(p);
  }
}).withConfig({
  displayName: "GridItem"
})(["", " &&{", "}"], pseudoFlexStyles, gridItemStyles);
GridItem.displayName = 'Grid.Item';
var ColumnPropType = PropTypes.oneOf(COLUMN_NUMS);
GridItem.propTypes = {
  tiny: ColumnPropType,
  small: ColumnPropType,
  medium: ColumnPropType,
  large: ColumnPropType,
  extraLarge: ColumnPropType
};
export var Grid = function () {
  var repeat = function repeat(count, text) {
    if (!isIE) return "repeat(".concat(count, ", ").concat(text, ")");
    var result = [];

    for (var i = 0; i < count; i++) {
      result.push(text);
    }

    return result.join(' ');
  }; // Shortcut, e.g. so you can just say `cols={4}` and it'll add 4 equal columns.


  var templateTransform = function templateTransform(defaultSize) {
    return function (val) {
      return typeof val === 'number' ? repeat(val, defaultSize) : val;
    };
  };

  var gridStyleConfig = {
    rows: {
      property: css.gridTemplateRows,
      transform: templateTransform('min-content')
    },
    cols: {
      property: css.gridTemplateColumns,
      transform: templateTransform('1fr')
    },
    justifyItems: {
      property: 'justifyItems'
    },
    autoFlow: {
      property: 'gridAutoFlow'
    },
    autoRows: {
      property: 'gridAutoRows'
    },
    autoCols: {
      property: 'gridAutoColumns'
    },
    grid: true,
    gridTemplate: true,
    gridTemplateAreas: true
  };
  gridStyleConfig.justify = gridStyleConfig.justifyItems;
  gridStyleConfig.columns = gridStyleConfig.cols;
  gridStyleConfig.autoColumns = gridStyleConfig.autoCols;
  var gridTag;
  var justifyStyles;

  if (isIE) {
    // Technically there are other indicator props, but none are supported in any
    // way by IE, so if going for IE-compatible they won't be using those anyway.
    var isPseudoFlexMode = function isPseudoFlexMode(props) {
      return {
        isPseudoFlex: !(props.rows || props.cols || props.columns || props.gridAreas)
      };
    };

    var gridBase = /*#__PURE__*/React.forwardRef(function (_ref3, inRef) {
      var isPseudoFlex = _ref3.isPseudoFlex,
          props = _objectWithoutProperties(_ref3, _excluded);

      // Make sure we re-run the effect when screen size changes.
      useScreenSize();
      var ref = useMergedRefs(inRef);
      React.useLayoutEffect(function () {
        // IE doesn't auto-fill grids, so we have to do it ourselves.
        if (ref.current && isPseudoFlex) {
          var column = 0; // The math's a bit easier starting from zero.

          var row = 1;
          var gridItems = ref.current.children;

          for (var i = 0; i < gridItems.length; i++) {
            var item = gridItems[i];
            var span = parseInt(getComputedStyle(item)[css.gridColumnEnd]);

            if (span + column > PSEUDO_FLEX_COLS) {
              row += 1;
              column = 0;
            }

            item.style[css.gridRowStart] = String(row);
            item.style[css.gridColumnStart] = String(column + 1);
            column += span;
          }

          ref.current.style[css.gridTemplateRows] = repeat(row, 'min-content');
        }
      });
      return /*#__PURE__*/React.createElement(Flex, Object.assign({}, props, {
        ref: ref
      }));
    });
    gridTag = styled(gridBase).attrs(isPseudoFlexMode);

    var applyJustifyToChildren = function applyJustifyToChildren(value) {
      if (value) return {
        '& > *': _defineProperty({}, css.justifySelf, value)
      };
    }; // Keeping this separate in case we implement inline styles, see CACTUS-975.


    justifyStyles = SS.system({
      justify: applyJustifyToChildren,
      justifyItems: applyJustifyToChildren,
      alignItems: function alignItems(value) {
        // Note the key is slightly different to prevent overwriting
        // if both `alignItems` & `justifyItems` are passed.
        if (value) return {
          '&>*': _defineProperty({}, css.alignSelf, value)
        };
      }
    });
  } else {
    gridTag = styled(Flex); // Could be a constant, but it makes things more symmetrical with the IE code.

    justifyStyles = function justifyStyles() {
      return {
        justifyItems: 'normal'
      };
    };
  }

  var pseudoFlexColumns = repeat(PSEUDO_FLEX_COLS, 'minmax(1px, 1fr)');
  var systemStyles = SS.system(gridStyleConfig);

  var gridStyles = function gridStyles(props) {
    var styles = justifyStyles(props);
    styles.width = '100%';
    styles.display = css.grid;
    styles[css.gridTemplateColumns] = pseudoFlexColumns;
    styles['&&'] = systemStyles(props);
    var gridAreas = props.gridAreas;

    if (gridAreas) {
      for (var _i = 0, _Object$keys = Object.keys(gridAreas); _i < _Object$keys.length; _i++) {
        var className = _Object$keys[_i];
        styles[".".concat(className)] = gridItemStyles({
          gridArea: gridAreas[className],
          theme: props.theme
        });
      }
    }

    return styles;
  };

  var styleProps = getOmittableProps(systemStyles, 'gridAreas');
  return gridTag.withConfig({
    displayName: 'Grid',
    shouldForwardProp: function shouldForwardProp(p) {
      return !styleProps.has(p);
    }
  })(gridStyles);
}();
Grid.defaultProps = {
  gap: DEFAULT_GAP
};
Grid.Item = GridItem;
Grid.supportsGap = Flex.supportsGap;
Grid.supportsGrid = typeof CSS !== 'undefined' && ((_CSS$supports = (_CSS = CSS).supports) === null || _CSS$supports === void 0 ? void 0 : _CSS$supports.call(_CSS, 'display', 'grid'));
export default Grid;